import React, { useEffect, useState } from 'react';
import {
  FlexBox,
  IconButton,
  IconFontAwesome,
  InputDateRange
} from '@directsoftware/ui-kit-web-admin';
import { useSelector } from 'react-redux';
import { isInclusivelyBeforeDay } from 'react-dates';
import { calculateMaxes, isOutsideRange } from '../resources/shared-functions';
import { selectListingsSearchState } from '../../../redux/slices/listingsSearch';
import { useDetectMobile } from '../../../../shared/hooks/useDetectMobile';

const ListingsSearchDateRange = ({ updateSearchState, translate }) => {
  const brand = useSelector(state => state.brand);
  const { isLargeDesktop, isMobile } = useDetectMobile();
  const dateFormat = useSelector(state => state.brand.date_format);
  const searchState = useSelector(selectListingsSearchState);
  const [localStartDate, setLocalStartDate] = useState(null);
  const [localEndDate, setLocalEndDate] = useState(null);
  const [localFocusedInput, setLocalFocusedInput] = useState(null);

  const adjustDateFormat = () => {
    if (dateFormat === 'DD/MM/YYYY') {
      return dateFormat
        .replace('/', ' ')
        .replace('/', ' ')
        .replace('MM', 'MMM');
    }

    return dateFormat
      .replace('/', ' ')
      .replace('/', ' ')
      .replace('MM', 'MMM')
      .replace('DD', 'DD,');
  };

  const respondToDatesChange = (checkInDate, checkOutDate) => {
    if (isInclusivelyBeforeDay(checkInDate, checkOutDate)) {
      const bookingRange = [];
      const d = checkInDate.clone();
      while (isInclusivelyBeforeDay(d, checkOutDate)) {
        bookingRange.push({
          key: d.format('DD-MM-YYYY'),
          day: d.day()
        });
        d.add(1, 'days');
      }
      updateSearchState(
        {
          bookingRange,
          bookingLength: bookingRange.length - 1,
          checkIn: checkInDate,
          checkOut: checkOutDate
        },
        true
      );
      calculateMaxes(searchState, updateSearchState);
    } else if (!checkInDate && !checkOutDate) {
      updateSearchState(
        {
          bookingRange: null,
          bookingLength: 0,
          checkIn: null,
          checkOut: null
        },
        true
      );
    }
  };

  // Keep the picker's local state in sync with the authoritative search state
  // (seeded from the URL by SearchV2, and updated on clear / browser back).
  // Without this the inputs are seeded once and never reflect a later clear,
  // leaving stale dates in the field even though the search state was cleared.
  // Guarded by focus so an in-progress selection is never disturbed.
  useEffect(
    () => {
      if (localFocusedInput) return;
      setLocalStartDate(searchState.checkIn || null);
      setLocalEndDate(searchState.checkOut || null);
    },
    [searchState.checkIn, searchState.checkOut, localFocusedInput]
  );

  // The shared ui-kit InputDateRange forwards the built-in react-dates clear
  // control under a misspelled prop, so its "×" never renders. Provide an
  // explicit clear control that nulls the dates and re-fetches the dateless
  // catalog. respondToDatesChange with both dates null resets search state and
  // triggers a re-fetch; the sync effect above then clears the local inputs.
  const clearDates = () => {
    setLocalStartDate(null);
    setLocalEndDate(null);
    setLocalFocusedInput(null);
    respondToDatesChange(null, null);
  };

  const hasDates = Boolean(searchState.checkIn || localStartDate);

  useEffect(() => {
    setLocalStartDate(searchState.checkIn || null);
    setLocalEndDate(searchState.checkOut || null);
  }, [searchState.checkIn, searchState.checkOut]);

  return (
    <FlexBox alignItems="center" gap="xxs">
      <InputDateRange
        startDatePlaceholderText={
          brand.isRvFleet
            ? translate(`cx.global.pick_up`)
            : translate(`cx.global.check_in`)
        }
        endDatePlaceholderText={
          brand.isRvFleet
            ? translate(`cx.global.drop_off`)
            : translate(`cx.global.check_out`)
        }
        focusedInput={localFocusedInput}
        startDate={localStartDate}
        endDate={localEndDate}
        onDatesChange={({ startDate, endDate }) => {
          setLocalStartDate(startDate);
          setLocalEndDate(endDate);
          respondToDatesChange(startDate, endDate);
        }}
        onFocusChange={focusedInput => setLocalFocusedInput(focusedInput)}
        isDense
        inputWidth={isLargeDesktop ? 'xl' : 'l'}
        removeBottomSpacer
        isOutsideRange={isOutsideRange}
        displayFormat={adjustDateFormat()}
        orientation={isMobile ? 'vertical' : 'horizontal'}
        withFullScreenPortal={isMobile}
        readOnly
      />
      {hasDates && (
        <IconButton
          onClick={clearDates}
          variation="secondary"
          appearance="ghost"
          size="dense"
        >
          <IconFontAwesome name="times" iconStyle="regular" />
        </IconButton>
      )}
    </FlexBox>
  );
};

export default ListingsSearchDateRange;
